import React, { ReactNode, useCallback, useEffect, useState } from 'react';
import { Alert, Button, Modal, Radio, Slider, Spin, Tooltip } from 'antd';
import styled from 'styled-components';

import { presentationsApi } from '../../../api/presentationsApi';
import { IMapPreview, MAP_TYPES } from '../../../types/models/presentations';

/**
 * Adjust the location map on a listing's brochure.
 *
 * The trigger-as-children convention, same as `FloorPlanWizard` and
 * `RecordGalleriesModal`, so this drops into any row action or menu item.
 *
 * Unlike the floor-plan wizard there is no publish step, and that is the point:
 * a map is a pure function of its parameters, so the default is to accept what
 * comes back automatically and this exists only for the listing where the
 * framing is wrong. There is nothing to approve and no version history — the
 * saved settings *are* the map.
 */

const ZOOM_RANGE: [number, number] = [10, 18];

/** Slide 7's map box is 9.83 x 5.19in. Matching it means what someone sees here
 *  is framed exactly as the brochure will be, not a differently-shaped crop. */
const PREVIEW_ASPECT = 1.896;

const Body = styled.div`
  .preview {
    position: relative;
    width: 100%;
    aspect-ratio: ${PREVIEW_ASPECT};
    background: #f5f5f5;
    border: 1px solid #f0f0f0;
    border-radius: 2px;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
  }

  .preview img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  .controls {
    margin-top: 20px;
    display: grid;
    grid-template-columns: 90px 1fr;
    row-gap: 16px;
    align-items: center;
  }

  .label {
    color: rgba(0, 0, 0, 0.65);
  }

  .dirty {
    margin-top: 12px;
  }
`;

export interface MapImageEditorProps {
  listingId: number;
  onSaved?: (settings: { zoom: number | null; map_type: string }) => void;
  onDirtyChange?: (dirty: boolean) => void;
  /** In a modal: offered as Close, and called after a save. */
  onClose?: () => void;
}

export interface MapImageModalProps extends Omit<MapImageEditorProps, 'onClose' | 'onDirtyChange'> {
  /** Shown in the header. */
  listingName?: string;
  /** The trigger. Same convention as FloorPlanWizard. */
  children?: ReactNode;
}

/** The preview and its controls, with their own Save. Loads when mounted. */
export const MapImageEditor = ({
  listingId, onSaved, onDirtyChange, onClose,
}: MapImageEditorProps) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();
  const [preview, setPreview] = useState<IMapPreview>();
  // What the controls currently show. Starts at the saved values and diverges
  // as someone experiments; `dirty` below is the difference.
  const [zoom, setZoom] = useState<number>();
  const [mapType, setMapType] = useState<string>();

  const load = useCallback(async (params: { zoom?: number; map_type?: string }) => {
    setLoading(true);
    setError(undefined);
    try {
      const result = await presentationsApi.mapPreview(listingId, params);
      setPreview(result);
      return result;
    } catch (e: any) {
      setError(e?.message ?? 'Could not load the map.');
      return undefined;
    } finally {
      setLoading(false);
    }
  }, [listingId]);

  useEffect(() => {
    // Opening shows what the brochure would use today, with no parameters of
    // our own — so the first thing on screen is the truth, not a guess.
    load({}).then((result) => {
      if (!result) return;
      setZoom(result.saved.zoom);
      setMapType(result.saved.map_type);
    });
  }, [load]);

  const dirty = !!preview
    && (zoom !== preview.saved.zoom || mapType !== preview.saved.map_type);

  useEffect(() => { onDirtyChange?.(dirty); }, [dirty, onDirtyChange]);

  const regenerate = () => load({ zoom, map_type: mapType });

  const save = async () => {
    setSaving(true);
    try {
      const saved = await presentationsApi.saveMapSettings(listingId, {
        zoom, map_type: mapType,
      });
      setPreview((current) => current && {
        ...current,
        saved: { ...current.saved, zoom: saved.zoom ?? current.saved.zoom, map_type: saved.map_type },
      });
      onSaved?.({ zoom: saved.zoom, map_type: saved.map_type });
      onClose?.();
    } catch (e: any) {
      setError(e?.message ?? 'Could not save.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
        <Body>
          {error && (
            <Alert type="error" message={error} style={{ marginBottom: 16 }} />
          )}

          {preview && !preview.has_coordinates && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message="This property has no coordinates"
              description={
                'Without a latitude and longitude there is nothing to centre a '
                + 'map on, so the brochure will be generated without one. Set '
                + 'them on the listing first.'
              }
            />
          )}

          {preview?.has_custom_image && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message="This property uses an uploaded image"
              description={
                'Someone replaced the generated map by hand, so the settings '
                + 'below have no effect until that image is removed.'
              }
            />
          )}

          <div className="preview">
            {loading && <Spin />}
            {!loading && preview?.url && (
              <img src={preview.url} alt="Location map as it will appear in the brochure" />
            )}
            {!loading && !preview?.url && !error && (
              <span className="label">No map available.</span>
            )}
          </div>

          <div className="controls">
            <span className="label">Zoom</span>
            <Slider
              min={ZOOM_RANGE[0]}
              max={ZOOM_RANGE[1]}
              value={zoom}
              onChange={setZoom}
              onAfterChange={() => regenerate()}
              marks={{
                [ZOOM_RANGE[0]]: 'wider',
                [ZOOM_RANGE[1]]: 'closer',
              }}
              disabled={!preview?.has_coordinates}
            />

            <span className="label">Style</span>
            <Radio.Group
              value={mapType}
              onChange={(e) => {
                setMapType(e.target.value);
                load({ zoom, map_type: e.target.value });
              }}
              optionType="button"
              buttonStyle="solid"
              disabled={!preview?.has_coordinates}
            >
              {MAP_TYPES.map((type) => (
                <Radio.Button key={type} value={type}>
                  {type === 'roadmap' ? 'Road' : 'Satellite'}
                </Radio.Button>
              ))}
            </Radio.Group>
          </div>

          {dirty && (
            <Alert
              className="dirty"
              type="info"
              showIcon
              message="Unsaved — the brochure still uses the previous setting until you save."
            />
          )}
        </Body>
        <Footer>
          {onClose && <Button onClick={onClose}>Close</Button>}
          <Tooltip title={dirty ? '' : 'Nothing changed yet.'}>
            <Button type="primary" onClick={save} loading={saving} disabled={!dirty}>
              {onClose ? 'Save' : 'Save map'}
            </Button>
          </Tooltip>
        </Footer>
    </>
  );
};

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 16px;
`;

const MapImageModal = ({ listingName, children, ...editor }: MapImageModalProps) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <span onClick={() => setOpen(true)}>{children}</span>
      <Modal
        visible={open}
        onCancel={() => setOpen(false)}
        title={`Map image${listingName ? ` — ${listingName}` : ''}`}
        width={720}
        destroyOnClose
        footer={null}
      >
        {open && <MapImageEditor {...editor} onClose={() => setOpen(false)} />}
      </Modal>
    </>
  );
};

export default MapImageModal;
